import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  Shield, Award, Lock, CheckCircle, Clock, BadgeCheck, AlertCircle,
  Building2, Mail, FileText, ExternalLink, ChevronDown, ChevronUp,
  FileSearch, Calendar, Loader2, Search, Eye, Download, Settings,
  BarChart3, Globe, Key, ShieldCheck, Users, Database, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getApiBaseUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'compliance.cyberforte.com.au' || hostname === 'www.compliance.cyberforte.com.au') {
    return '';
  }
  return BACKEND_URL || '';
};

const API = `${getApiBaseUrl()}/api`;

export default function PublicTrustCenterPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    compliance: true,
    documents: false,
    riskProfile: true,
    productSecurity: true
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [accessRequest, setAccessRequest] = useState(null); // { type, resource } or null when closed
  const [accessForm, setAccessForm] = useState({ email: '', name: '', company: '', message: '' });
  const [submittingAccess, setSubmittingAccess] = useState(false);
  const [accessResult, setAccessResult] = useState(null);

  const openAccessRequest = (type, resource = null) => {
    setAccessRequest({ type, resource });
    setAccessForm({ email: '', name: '', company: '', message: '' });
    setAccessResult(null);
  };

  const submitAccessRequest = async () => {
    if (!data?.tenant_id) return;
    if (!accessForm.email || !accessForm.email.includes('@')) {
      setAccessResult({ error: 'A valid email is required' });
      return;
    }
    setSubmittingAccess(true);
    try {
      const body = {
        tenant_id: data.tenant_id,
        resource_type: accessRequest.type,               // 'policy' | 'document' | 'general'
        resource_id: accessRequest.resource?.id || null,
        resource_title: accessRequest.resource?.title || accessRequest.resource?.name || null,
        requester_email: accessForm.email.trim(),
        requester_name: accessForm.name.trim() || null,
        company: accessForm.company.trim() || null,
        message: accessForm.message.trim() || null,
      };
      const res = await axios.post(`${API}/trust-center/access-request`, body);
      setAccessResult({ ok: true, message: res.data.message });
    } catch (err) {
      setAccessResult({ error: err.response?.data?.detail || 'Failed to submit request' });
    } finally {
      setSubmittingAccess(false);
    }
  };

  useEffect(() => {
    fetchTrustCenterData();
  }, [slug]);

  const fetchTrustCenterData = async () => {
    try {
      const response = await axios.get(`${API}/trust-center/public/${slug}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch trust center:', err);
      if (err.response?.status === 404) {
        setError({
          title: 'Trust Center Not Found',
          message: 'The Trust Center you are looking for does not exist or has been removed.'
        });
      } else if (err.response?.status === 403) {
        setError({
          title: 'Access Denied',
          message: 'This Trust Center is not publicly accessible.'
        });
      } else {
        setError({
          title: 'Failed to Load',
          message: 'Unable to load the Trust Center. Please try again later.'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatFrameworkName = (framework) => {
    const names = {
      'ISO27001': 'ISO/IEC 27001',
      'SOC2': 'SOC 2 Type II',
      'Essential8': 'Essential 8',
      'PCIDSS': 'PCI DSS',
      'HIPAA': 'HIPAA',
      'ISO42001': 'ISO/IEC 42001'
    };
    return names[framework] || framework;
  };

  const getComplianceIcon = (framework) => {
    return (
      <div className="w-10 h-10 rounded-lg bg-blue-600 flex items-center justify-center">
        <span className="text-white font-bold text-xs">ISO</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading Trust Center...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md w-full mx-4 shadow-lg">
          <CardContent className="pt-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-2">{error.title}</h2>
            <p className="text-slate-600">{error.message}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            {data.organization_logo ? (
              <img 
                src={data.organization_logo} 
                alt={data.organization_name} 
                className="h-10 w-auto object-contain"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm">
                  {data.organization_name?.charAt(0) || 'T'}
                </span>
              </div>
            )}
            <div>
              <h1 className="text-xl font-semibold text-slate-900">{data.organization_name || 'Trust Center'}</h1>
              <p className="text-xs text-slate-500">Trust Center</p>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-white border-b-4 border-teal-600">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">
                {data.hero_title || `Welcome to ${data.organization_name}`}
              </h2>
              <p className="text-slate-600">
                {data.hero_subtitle || 'Access our security documentation and compliance reports'}
              </p>
            </div>
            {data.contact_email && (
              <a 
                href={`mailto:${data.contact_email}?subject=Security Information Request`}
                className="inline-flex items-center gap-2 text-teal-700 hover:text-teal-800 font-medium text-sm"
              >
                <Mail className="w-4 h-4" />
                Ask for information
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            type="text"
            placeholder="Search content"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 pr-20 py-3 bg-white border-slate-200 rounded-lg shadow-sm text-base"
            data-testid="search-input"
          />
          <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded">
            ctrl K
          </span>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-6xl mx-auto px-4 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          
          {/* Overview Card */}
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Overview</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('overview')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.overview ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.overview && (
              <CardContent className="pt-2">
                <p className="text-slate-600 text-sm leading-relaxed">
                  {data.custom_message || `Welcome to ${data.organization_name}'s Trust Center. We are committed to maintaining the highest standards of security, privacy, and compliance.`}
                </p>
              </CardContent>
            )}
          </Card>

          {/* Compliance Card */}
          {data.visible_sections?.certifications !== false && (
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <BadgeCheck className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Compliance</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('compliance')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.compliance ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.compliance && (
              <CardContent className="pt-2">
                {/* Certifications */}
                {data.certifications && data.certifications.length > 0 ? (
                  <div className="space-y-3">
                    {data.certifications.map((cert, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          cert.framework?.toLowerCase().includes('iso') ? 'bg-blue-600' :
                          cert.framework?.toLowerCase().includes('soc') ? 'bg-purple-600' :
                          cert.framework?.toLowerCase().includes('essential') ? 'bg-green-600' :
                          cert.framework?.toLowerCase().includes('pci') ? 'bg-orange-600' :
                          cert.framework?.toLowerCase().includes('hipaa') ? 'bg-red-600' :
                          cert.framework?.toLowerCase().includes('gdpr') ? 'bg-indigo-600' :
                          cert.framework?.toLowerCase().includes('nist') ? 'bg-teal-600' :
                          'bg-slate-600'
                        }`}>
                          <span className="text-white font-bold text-xs">
                            {cert.framework?.toLowerCase().includes('iso') ? 'ISO' :
                             cert.framework?.toLowerCase().includes('soc') ? 'SOC' :
                             cert.framework?.toLowerCase().includes('essential') ? 'E8' :
                             cert.framework?.toLowerCase().includes('pci') ? 'PCI' :
                             cert.framework?.toLowerCase().includes('hipaa') ? 'HIP' :
                             cert.framework?.toLowerCase().includes('gdpr') ? 'GDPR' :
                             cert.framework?.toLowerCase().includes('nist') ? 'NIST' :
                             cert.framework?.substring(0, 3).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{cert.framework}</span>
                            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-sm">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                          {cert.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{cert.description}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {data.compliance_coverage && data.compliance_coverage.filter(c => c.compliance_percentage > 0).slice(0, 3).map((coverage, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          coverage.framework?.toLowerCase().includes('iso') ? 'bg-blue-600' :
                          coverage.framework?.toLowerCase().includes('soc') ? 'bg-purple-600' :
                          coverage.framework?.toLowerCase().includes('essential') ? 'bg-green-600' :
                          coverage.framework?.toLowerCase().includes('pci') ? 'bg-orange-600' :
                          coverage.framework?.toLowerCase().includes('hipaa') ? 'bg-red-600' :
                          coverage.framework?.toLowerCase().includes('gdpr') ? 'bg-indigo-600' :
                          coverage.framework?.toLowerCase().includes('nist') ? 'bg-teal-600' :
                          'bg-slate-600'
                        }`}>
                          <span className="text-white font-bold text-xs">
                            {coverage.framework?.toLowerCase().includes('iso') ? 'ISO' :
                             coverage.framework?.toLowerCase().includes('soc') ? 'SOC' :
                             coverage.framework?.toLowerCase().includes('essential') ? 'E8' :
                             coverage.framework?.toLowerCase().includes('pci') ? 'PCI' :
                             coverage.framework?.toLowerCase().includes('hipaa') ? 'HIP' :
                             coverage.framework?.toLowerCase().includes('gdpr') ? 'GDPR' :
                             coverage.framework?.toLowerCase().includes('nist') ? 'NIST' :
                             coverage.framework?.substring(0, 3).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{formatFrameworkName(coverage.framework)}</span>
                            <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow-sm">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                          </div>
                          <p className="text-xs text-slate-500">{coverage.compliance_percentage}% compliant</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            )}
          </Card>
          )}

          {/* Trusted By Section - Full Width (Optional) */}
          {data.visible_sections?.trusted_by !== false && (
            <div className="lg:col-span-2 bg-slate-50 rounded-lg py-8 px-6 text-center">
              <p className="text-sm text-slate-600 mb-4">
                <span className="font-semibold text-slate-900">{data.organization_name}</span> is reviewed and trusted by
              </p>
              <div className="flex items-center justify-center gap-8">
                {data.organization_logo ? (
                  <img 
                    src={data.organization_logo} 
                    alt={data.organization_name}
                    className="h-16 w-auto object-contain opacity-70"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-700 flex items-center justify-center">
                    <span className="text-white font-bold text-xl">
                      {data.organization_name?.charAt(0) || 'T'}
                    </span>
                  </div>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-3">{data.organization_name}</p>
            </div>
          )}

          {/* Documents Card */}
          {data.visible_sections?.documents !== false && ((data.documents && data.documents.length > 0) || (data.pentest_reports && data.pentest_reports.length > 0)) && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Documents</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="flex flex-col gap-3">
                  {data.pentest_reports && data.pentest_reports.map((report, index) => (
                    <div 
                      key={`pentest-${index}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="w-10 h-10 rounded bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <FileSearch className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">Reports</p>
                        <p className="text-sm font-medium text-slate-900 break-words">{report.title || 'Pentest Report'}</p>
                      </div>
                      <Lock className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </div>
                  ))}
                  {data.documents && data.documents.map((doc, index) => (
                    <div
                      key={`doc-${index}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                      data-testid={`public-doc-${doc.source || 'file'}-${doc.id || index}`}
                    >
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 shadow-sm">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="w-10 h-10 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400 uppercase tracking-wide">
                          {doc.source === 'certification' ? 'Certificate' : doc.source === 'pentest' ? 'Pentest Report' : 'Document'}
                        </p>
                        <p className="text-sm font-medium text-slate-900 break-words">{doc.title || doc.name || doc.file_name}</p>
                        {doc.file_name && (doc.title || doc.name) && doc.file_name !== (doc.title || doc.name) && (
                          <p className="text-xs text-slate-500 break-words mt-0.5">{doc.file_name}</p>
                        )}
                      </div>
                      {(doc.locked || doc.source === 'certification' || doc.source === 'pentest') ? (
                        <span
                          className="flex items-center gap-1 rounded-full bg-slate-100 border border-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600"
                          title="Request access to view or download"
                        >
                          <Lock className="w-3 h-3" /> Locked
                        </span>
                      ) : doc.url ? (
                        <a href={doc.url} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 text-slate-400 hover:text-teal-600" />
                        </a>
                      ) : (
                        <Lock className="w-4 h-4 text-slate-400" />
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Risk Profile Card - Next to Documents */}
          {data.visible_sections?.risk_profile !== false && (
          <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <Shield className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Risk Profile</CardTitle>
                </div>
                <button 
                  onClick={() => toggleSection('riskProfile')}
                  className="p-1 hover:bg-slate-100 rounded"
                >
                  {expandedSections.riskProfile ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </button>
              </div>
            </CardHeader>
            {expandedSections.riskProfile && (
              <CardContent className="pt-2">
                <div className="space-y-3">
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Data Access Level</span>
                    </div>
                    <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                      data.risk_profile?.data_access_level?.value === 'Public' ? 'bg-green-100 text-green-700' :
                      data.risk_profile?.data_access_level?.value === 'Internal' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {data.risk_profile?.data_access_level?.value || 'Public'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Impact Level</span>
                    </div>
                    <span className={`text-sm font-medium px-2 py-0.5 rounded ${
                      data.risk_profile?.impact_level?.value === 'Low' ? 'bg-green-100 text-green-700' :
                      data.risk_profile?.impact_level?.value === 'Moderate' ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {data.risk_profile?.impact_level?.value || 'Moderate'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-slate-400" />
                      <span className="text-sm text-slate-600">Recovery Time Objective</span>
                    </div>
                    <span className="text-sm font-medium text-slate-900">
                      {data.risk_profile?.recovery_time_objective?.value || '10 hours'}
                    </span>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
          )}

          {/* Policies Section - Grid Layout */}
          {data.visible_sections?.policies !== false && data.policies && data.policies.length > 0 && (
            <Card className="bg-white shadow-sm border-slate-200 hover:shadow-md transition-shadow lg:col-span-2">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-slate-600" />
                  </div>
                  <CardTitle className="text-lg font-semibold text-slate-900">Policies & Procedures</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {data.policies.map((policy, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100"
                    >
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                        <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{policy.title || policy.name}</p>
                        {policy.category && (
                          <span className="text-xs text-slate-500">{policy.category}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Compliance Coverage - Separate Cards */}
          {data.visible_sections?.compliance_coverage !== false && data.compliance_coverage && data.compliance_coverage.length > 0 && (
            <>
              <div className="lg:col-span-2 mb-2">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-slate-600" />
                  Compliance Coverage
                </h3>
              </div>
              {data.compliance_coverage.map((framework, index) => {
                // Framework-specific icon mapping
                const getFrameworkIcon = (name) => {
                  const frameworkLower = name?.toLowerCase() || '';
                  if (frameworkLower.includes('iso')) return <ShieldCheck className="w-5 h-5 text-blue-600" />;
                  if (frameworkLower.includes('soc')) return <Award className="w-5 h-5 text-purple-600" />;
                  if (frameworkLower.includes('essential')) return <Shield className="w-5 h-5 text-green-600" />;
                  if (frameworkLower.includes('pci')) return <Lock className="w-5 h-5 text-orange-600" />;
                  if (frameworkLower.includes('hipaa')) return <FileText className="w-5 h-5 text-red-600" />;
                  if (frameworkLower.includes('gdpr')) return <Users className="w-5 h-5 text-indigo-600" />;
                  if (frameworkLower.includes('nist')) return <Database className="w-5 h-5 text-teal-600" />;
                  return <Shield className="w-5 h-5 text-slate-600" />;
                };
                
                const getFrameworkColor = (name) => {
                  const frameworkLower = name?.toLowerCase() || '';
                  if (frameworkLower.includes('iso')) return 'bg-blue-50 border-blue-200';
                  if (frameworkLower.includes('soc')) return 'bg-purple-50 border-purple-200';
                  if (frameworkLower.includes('essential')) return 'bg-green-50 border-green-200';
                  if (frameworkLower.includes('pci')) return 'bg-orange-50 border-orange-200';
                  if (frameworkLower.includes('hipaa')) return 'bg-red-50 border-red-200';
                  if (frameworkLower.includes('gdpr')) return 'bg-indigo-50 border-indigo-200';
                  if (frameworkLower.includes('nist')) return 'bg-teal-50 border-teal-200';
                  return 'bg-slate-50 border-slate-200';
                };
                
                return (
                  <Card key={index} className={`shadow-sm border hover:shadow-md transition-shadow ${getFrameworkColor(framework.framework)}`}>
                    <CardContent className="pt-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          {getFrameworkIcon(framework.framework)}
                        </div>
                        <div>
                          <h4 className="font-semibold text-slate-900">{formatFrameworkName(framework.framework)}</h4>
                          <span className="text-xs text-slate-500">{framework.compliant_controls} / {framework.total_controls} controls</span>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Progress value={framework.compliance_percentage} className="h-2" />
                        <div className="flex justify-between text-xs">
                          <span className="text-slate-500">Compliance</span>
                          <span className="font-semibold text-slate-900">{framework.compliance_percentage}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}

          {/* Security Practices - Separate Cards for Each Category */}
          {data.visible_sections?.security_practices !== false && data.security_practices && data.security_practices.length > 0 && (
            <>
              <div className="lg:col-span-2 mb-2 mt-4">
                <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-slate-600" />
                  Security Practices
                </h3>
              </div>
              {data.security_practices.map((practice, index) => {
                // Category-specific icon mapping
                const getCategoryIcon = (category) => {
                  const categoryLower = category?.toLowerCase() || '';
                  if (categoryLower.includes('data')) return <Database className="w-5 h-5 text-blue-600" />;
                  if (categoryLower.includes('access')) return <Key className="w-5 h-5 text-purple-600" />;
                  if (categoryLower.includes('monitor')) return <Eye className="w-5 h-5 text-green-600" />;
                  if (categoryLower.includes('network')) return <Globe className="w-5 h-5 text-orange-600" />;
                  if (categoryLower.includes('incident')) return <AlertTriangle className="w-5 h-5 text-red-600" />;
                  if (categoryLower.includes('encrypt')) return <Lock className="w-5 h-5 text-indigo-600" />;
                  return <Shield className="w-5 h-5 text-slate-600" />;
                };
                
                const getCategoryColor = (category) => {
                  return 'bg-white border-slate-200';
                };
                
                return (
                  <Card key={index} className={`shadow-sm border hover:shadow-md transition-shadow ${getCategoryColor(practice.category)}`}>
                    <CardContent className="pt-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                          {getCategoryIcon(practice.category)}
                        </div>
                        <h4 className="font-semibold text-slate-900">{practice.category}</h4>
                      </div>
                      <ul className="space-y-3">
                        {practice.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="flex items-start gap-3 text-sm text-slate-700">
                            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5 shadow-sm">
                              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 py-8 mt-auto">
        <div className="max-w-6xl mx-auto px-4">
          <a
            href="https://cyberforte.com.au"
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center gap-2 group"
            data-testid="footer-cyberforte-link"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_compliance-hub-261/artifacts/r86y6lnz_CyberForteIcon.png"
              alt="Cyber Forte"
              className="h-10 w-auto object-contain transition-transform group-hover:scale-105"
            />
            <span className="text-sm text-slate-500 group-hover:text-teal-700 transition-colors">
              Built on Comply by Cyber Forte
            </span>
          </a>
        </div>
      </footer>
    </div>
  );
}
